<template>
  <div class="flex justify-center w-full h-full">
    <div id="famtree-container" class="w-full h-full" v-if="!renderError">
      <AddTreeMember
        v-if="edit"
        :id="id + 'addnode-modal'"
        v-model="addNodeModal"
        :selecting="selecting"
        @update:modelValue="($event) => (addNodeModal = $event)"
        @saveEdit="saveAddNewNode"
        @cancelEdit="cancelAddNewNode"
        :people-options="memberNotInTreeComputed"
      />
      <EditTreeMember
        v-if="edit"
        :id="id + 'editnode-modal'"
        v-model="editNodeModal"
        :selecting="selecting"
        :people-options="memberNotInTreeComputed"
        @saveEdit="saveEditNode"
      />
      <div
        :id="id"
        class="flex-grow flex justify-center w-full h-full relative"
      >
        <div
          class="absolute z-10 flex flex-col justify-start items-start top-4 left-4 border-2 border-gray-400 rounded"
        >
          <button
            class="bg-white hover:bg-gray-300 border-0 px-2 h-8 w-full text-xl rounded-t-sm"
            @click="zoomIn"
          >
            +
          </button>
          <button
            class="bg-white hover:bg-gray-300 border-0 border-b-2 border-t-2 border-gray-400 px-2 h-8 w-full text-xl"
            @click="zoomOut"
          >
            -
          </button>
          <button
            class="bg-white hover:bg-gray-300 border-0 px-2 h-8 w-full rounded-b-sm"
            @click="centerNode"
          >
            <img src="@/assets/images/icon/fit.png" class="w-4 h-4" />
          </button>
        </div>
      </div>
      <!-- <div class="tree-menu-wrapper">
        <div class="tree-menu"></div>
      </div> -->
    </div>
    <div v-else>
      <p>เกิดข้อผิดพลาดกรุณาติดต่อเจ้าหน้าที่</p>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, watch, computed } from "vue";
import * as d3 from "d3";
import AddTreeMember from "./AddTreeMember.vue";
import EditTreeMember from "./EditTreeMember.vue";
import Swal from "sweetalert2";
import {
  createSVG,
  flatten,
  stratify,
  tree,
  createVerticalLine,
  dism,
  nodeSize,
  getCenterTransform,
  createTrashIcon,
  createEditIcon,
  createPlusIcon,
  findNodeAndDelete,
  addSameLevelNode,
  changeRelationSameLevel,
  findAndDeleteRelation,
  addChildNode,
  changeRelationChild,
  createRectMenuIcon,
  createRelationMenuIcon,
  createRectNode,
} from "./utils/helper";

const swalCustom = Swal.mixin({
  customClass: {
    confirmButton: "btn btn-primary m-1",
    cancelButton: "btn btn-outline-secondary m-1",
  },
  buttonsStyling: false,
});

const props = defineProps({
  edit: {
    type: Boolean,
    default: false,
  },
  id: { type: String, default: "family-tree" },
  zoomable: {
    type: Boolean,
    default: true,
  },
  treeData: {
    type: Array,
    default: () => [],
  },
  relationData: {
    type: Array,
    default: () => [],
  },
  member: {
    type: Array,
    default: () => [],
  },
});

const emits = defineEmits([
  "onTreeDataChange",
  "onRelationDataChange",
  "onclickNode",
]);

const treeDataComputed = computed({
  get() {
    return props.treeData;
  },
  set(value) {
    emits("onTreeDataChange", value);
  },
});

const relationDataComputed = computed({
  get() {
    return props.relationData;
  },
  set(value) {
    emits("onRelationDataChange", value);
  },
});

const treeCurrentData = ref([{ child: "root", hidden: true }]);
const relationCurrentData = ref([]);
const flatNode = ref(null);
const addNodeModal = ref(false);
const editNodeModal = ref(false);
const selecting = ref(null);
const createTreeTimeOut = ref();
const renderError = ref(false);
const groupOfNode = ref(null);
const zoom = d3
  .zoom()
  .extent([
    [0, 0],
    [dism.width, dism.height],
  ])
  .scaleExtent([0.1, 8]);

const memberNotInTreeComputed = computed(() => {
  if (props.member.length < 1) return [];
  const filterList = treeCurrentData.value.map((item) => {
    return item.child;
  });
  const result = props.member.filter((item) => !filterList.includes(item._id));
  return result.map((item) => ({
    label: `${item.title}${item.name} ${item.surname}`,
    value: item,
  }));
});

watch(treeDataComputed, () => {
  treeCurrentData.value = treeDataComputed.value;
});
watch(treeCurrentData, () => {
  clearTimeout(createTreeTimeOut.value);
  createTreeTimeOut.value = setTimeout(() => {
    createTree();
  }, 200);
});
watch(relationDataComputed, () => {
  relationCurrentData.value = relationDataComputed.value;
});
watch(relationCurrentData, () => {
  clearTimeout(createTreeTimeOut.value);
  createTreeTimeOut.value = setTimeout(() => {
    createTree();
  }, 200);
});
onMounted(() => {
  treeCurrentData.value = JSON.parse(JSON.stringify(treeDataComputed.value));
  relationCurrentData.value = JSON.parse(
    JSON.stringify(relationDataComputed.value)
  );

  createTree();
});

const createTree = async () => {
  try {
    const treeData = await prepareData();
    const [svg, g] = await createSVG(props.id);
    groupOfNode.value = g;

    // add verticalLine (line between level)
    const verticalLine = g
      .append("g")
      .selectAll("path")
      .data(treeData.links());

    verticalLine
      .enter()
      .append("path")
      .attr("d", createVerticalLine)
      .attr("class", "relation-line");

    // add text to verticalline
    const relationText = g
      .append("g")
      .selectAll("text")
      .data(treeData.links());
    relationText
      .enter()
      .append("text")
      .attr(
        "transform",
        (d) =>
          `translate(${d.target.x + 5 + nodeSize.width / 2}, ${d.target.y -
            30})`
      )
      .attr("class", "relation-text")
      .text((d) => d.target.data.relationWithParent)
      .attr("hidden", (d) => (d.target.data.no_parent ? true : undefined));

    // add horizonline (line in the same level)
    const horizonLine = g
      .append("g")
      .selectAll("path")
      .data(relationCurrentData.value);

    horizonLine
      .enter()
      .append("path")
      .attr("d", sblingLine)
      .attr("class", "relation-line");

    // group of node in tree
    const nodes = g
      .append("g")
      .selectAll("rect")
      .data(treeData.descendants());

    const enterNodes = createRectNode(nodes, handleClickNode);

    const relationAddNode = enterNodes
      .filter((d) => d.data.relation_node)
      .append("g")
      .attr(
        "transform",
        () => `translate(${nodeSize.width / 2 - 22.5}, ${nodeSize.height / 2})`
      )
      .attr("class", props.edit ? "cursor-pointer" : undefined)
      .on("click", props.edit ? handleClickMenu : null);

    relationAddNode
      .append("text")
      .text("คู่สมรส")
      .attr("class", "relation-text")
      .attr("transform", () => `translate(-12, -30)`);

    if (props.edit) {
      // สร้าง icon menu ให้ node relation
      createRelationMenuIcon(relationAddNode);

      // สร้าง icon menu ให้ node ทั่วไป
      const reactNodeMenu = enterNodes
        .filter((d) => !d.data.hidden)
        .append("g")
        .attr("class", "cursor-pointer")
        .attr("transform", `translate(${nodeSize.width - 45}, 10)`)
        .on("click", props.edit ? handleClickMenu : null);

      createRectMenuIcon(reactNodeMenu);
    }

    // center all node to center
    const transform = getCenterTransform(groupOfNode.value);
    groupOfNode.value.attr("transform", transform);
    // add zoomable
    if (props.zoomable) {
      svg
        .call(zoom)
        .call(zoom.transform, transform)
        .call(
          zoom.on("zoom", ({ transform }) => {
            groupOfNode.value.attr("transform", transform);
          })
        )
        .on("wheel.zoom", null);
    }
  } catch (error) {
    console.log(error);
    renderError.value = true;
  }
};

const zoomIn = () => {
  const svgNode = d3.select(groupOfNode.value.node().parentNode);
  svgNode.call(zoom.scaleBy, 1.25);
};
const zoomOut = () => {
  const svgNode = d3.select(groupOfNode.value.node().parentNode);
  svgNode.call(zoom.scaleBy, 0.75);
};

const centerNode = () => {
  const transform = getCenterTransform(groupOfNode.value);
  const svgNode = d3.select(groupOfNode.value.node().parentNode);
  svgNode.call(zoom.transform, transform);
  // groupOfNode.value.attr("transform", transform);
};

const prepareData = () => {
  let result = tree(stratify(treeCurrentData.value));
  flatNode.value = flatten(result);
  return result;
};
const sblingLine = (d, i) => {
  //start point
  const start = flatNode.value.filter((v) => d.source.id == v.id);
  //end point
  const end = flatNode.value.filter((v) => d.target.id == v.id);
  //define teh start coordinate and end co-ordinate
  const linedata = [
    {
      x: start[0].x,
      y: start[0].y,
    },
    {
      x: end[0].x,
      y: end[0].y,
    },
  ];
  const fun = d3
    .line()
    .x((d) => {
      return d.x;
    })
    .y((d) => {
      return d.y + nodeSize.height / 2;
    })
    .curve(d3.curveBasis);
  return fun(linedata);
};

const handleClickNode = (evt, d) => {
  emits("onclickNode", evt, d);
};

const handleClickMenu = (evt, d) => {
  selecting.value = d;
  addDropdownMenu(evt.clientY, evt.clientX);
};

const changeOpenModal = (bool) => {
  addNodeModal.value = bool;
};

const saveAddNewNode = async (data, relation) => {
  let temp = JSON.parse(JSON.stringify(treeDataComputed.value));
  let tempRelation = JSON.parse(JSON.stringify(relationDataComputed.value));
  const relationWith = selecting.value.data;
  switch (relation) {
    case "บุตร":
    case "หลาน":
    case "ญาติพี่น้อง":
    case "ผู้อาศัย":
    case "อื่นๆ":
    case "บิดา":
    case "มารดา":
    case "ลุง":
    case "ป้า":
    case "น้า":
    case "อา":
    case "ปู่":
    case "ย่า":
    case "ตา":
    case "ยาย":
      temp = await addChildNode(temp, data, relation, relationWith.child);
      // "บิดา" "มารดา" "ลุง" "ป้า" "น้า" "อา" "ปู่" "ย่า" "ตา" "ยาย"
      // addParentNode(data, relation);
      break;
    case "คู่สมรส":
      [temp, tempRelation] = await addSameLevelNode(
        temp,
        tempRelation,
        data,
        relation,
        relationWith
      );
      // createTree();
      break;
    default:
      break;
  }
  treeDataComputed.value = temp;
  relationDataComputed.value = tempRelation;
  selecting.value = null;
};

const fireCantHaveMultipleMarrySwal = () => {
  swalCustom.fire({
    icon: "error",
    html: `<div style="font-size:18px;"><b/>ไม่สามารถเพิ่มคู่ครองได้มากกว่าหนึ่งคน</div>`,
    timer: 4000,
  });
};

const changeAllParentNode = (parentName, data, tempData) => {
  const result = tempData.map((item) => {
    if (item.parent !== parentName) return item;
    item.parent = data._id;
    return item;
  });
  return result;
};

const cancelAddNewNode = () => {
  selecting.value = null;
};

const addDropdownMenu = (positionTop, positionLeft) => {
  const body = d3.select("body").attr("class", "relative main");

  const ulList = body
    .append("div")
    .attr("id", "tree-node-menu")
    .attr("class", "tree-node-menu")
    .on("click", removeTreeNodeMenu)
    .append("ul")
    .attr("style", `top: ${positionTop}px; left: ${positionLeft}px;`);

  const addMenuItem = ulList.append("li").on("click", addNewNode);
  createPlusIcon(addMenuItem);
  addMenuItem
    .append("p")
    .attr("class", "inline-block")
    .html("เพิ่ม");

  if (
    !selecting.value.data.relation_node &&
    !selecting.value.data.family_head
  ) {
    const editMenuItem = ulList.append("li").on("click", editNode);
    createEditIcon(editMenuItem);
    editMenuItem
      .append("p")
      .attr("class", "inline-block")
      .html("แก้ไข");
  }

  if (
    !selecting.value.data.relation_node &&
    !selecting.value.data.family_head
  ) {
    const deleteMenuItem = ulList.append("li").on("click", confirmDelete);
    createTrashIcon(deleteMenuItem);
    deleteMenuItem
      .append("p")
      .attr("class", "inline-block")
      .html("ลบ");
  }
};

const addNewNode = () => {
  changeOpenModal(true);
};
const editNode = () => {
  editNodeModal.value = true;
};
const deleteNode = async () => {
  if (
    selecting.value.data.relation_node &&
    selecting.value.data.data.family_head
  ) {
    selecting.value = null;
    return;
  }
  let targetId = selecting.value.id;
  let tempTreeData = await findNodeAndDelete(
    stratify(treeCurrentData.value),
    targetId
  );
  relationDataComputed.value = await findAndDeleteRelation(
    tempTreeData,
    relationCurrentData.value,
    targetId
  );
  treeDataComputed.value = tempTreeData;
  selecting.value = null;
};

const removeTreeNodeMenu = (evt) => {
  d3.select("#tree-node-menu").remove();
};

const confirmDelete = async () => {
  const swalRes = await swalCustom.fire({
    html: `<div style="font-size:18px;"><b/>คุณต้องการลบข้อมูล ${selecting.value.data.data.title}${selecting.value.data.data.name} ใช่หรือไม่</div>`,
    icon: "warning",
    showCancelButton: true,
    reverseButtons: true,
    confirmButtonText: "ตกลง",
    cancelButtonText: "ยกเลิก",
    allowOutsideClick: false,
  });
  if (swalRes.isConfirmed) deleteNode();
  else {
    selecting.value = null;
  }
};

const saveEditNode = async (changeNodeTo, changeRelationTo, changeParentTo) => {
  await changeRelationAndParent(changeRelationTo, changeParentTo);
  await changeMemberNode(changeNodeTo);
  selecting.value = null;
};

const changeMemberNode = async (changeTo) => {
  if (!changeTo) return;
  const changeThis = selecting.value.data;
  const treeTemp = JSON.parse(JSON.stringify(treeDataComputed.value));
  const relationTemp = JSON.parse(JSON.stringify(relationDataComputed.value));
  await treeTemp.forEach((item) => {
    if (item.child === changeThis.child) {
      item.child = changeTo._id;
      item.sex = changeTo.sex;
      item.data = changeTo;
    } else if (item.child.includes(changeThis.child)) {
      item.child = item.child.replace(changeThis.child, changeTo._id);
      if (item.data.source._id === changeThis.child) {
        item.data.source = changeTo;
      } else {
        item.data.target = changeTo;
      }
    }
    item.parent = item.parent.replace(changeThis.child, changeTo._id);
  });
  await relationTemp.forEach((item) => {
    item.source.id = item.source.id.replace(changeThis.child, changeTo._id);
    item.target.id = item.target.id.replace(changeThis.child, changeTo._id);
  });
  treeDataComputed.value = treeTemp;
  relationDataComputed.value = relationTemp;
};

const changeRelationAndParent = async (changeRelationTo, changeParentTo) => {
  const changeThis = selecting.value.data;
  let treeTemp = JSON.parse(JSON.stringify(treeCurrentData.value));
  let relationTemp = JSON.parse(JSON.stringify(relationCurrentData.value));
  if (changeParentTo) changeParent();
  switch (changeRelationTo) {
    case "บุตร":
    case "หลาน":
    case "ผู้อาศัย":
    case "ญาติพี่น้อง":
    case "อื่นๆ":
    case "บิดา":
    case "มารดา":
    case "ลุง":
    case "ป้า":
    case "น้า":
    case "อา":
    case "ปู่":
    case "ย่า":
    case "ตา":
    case "ยาย":
      [treeTemp, relationTemp] = await changeRelationChild(
        treeTemp,
        relationTemp,
        changeRelationTo,
        changeThis
      );
      break;
    case "คู่สมรส":
      if (changeThis.relationWithParent === "คู่สมรส") break;
      [treeTemp, relationTemp] = await changeRelationSameLevel(
        treeTemp,
        relationTemp,
        changeRelationTo,
        changeThis
      );
      break;
    default:
      break;
  }
  treeDataComputed.value = treeTemp;
  relationDataComputed.value = relationTemp;
};
</script>
